import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import * as AgentTypesActions from './agent-types.actions';
import {
  DELETE_AGENT_TYPE,
  GET_AGENT_TYPE,
  GET_PAGINATED_AGENT_TYPES,
  SAVE_AGENT_TYPE,
} from '../graphql/agent-types.graphql';
import {
  DeleteAgentTypeResponse,
  GetAgentTypeResponse,
  GetPaginatedAgentTypesResponse,
  SaveAgentTypeResponse,
} from '../responses/agent-types.response';

@Injectable({
  providedIn: 'root',
})
export class AgentTypesEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  $getPaginatedAgentTypes = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentTypesActions.AgentTypesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetPaginatedAgentTypesResponse>({
            query: GET_PAGINATED_AGENT_TYPES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageable: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getPaginatedAgentTypes;
              if (!payload) {
                return AgentTypesActions.AgentTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch agent types',
                    data: null,
                  },
                });
              }
              return AgentTypesActions.AgentTypesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                AgentTypesActions.AgentTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $saveAgentType = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentTypesActions.SaveAgentTypeAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<SaveAgentTypeResponse>({
            mutation: SAVE_AGENT_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.saveAgentType;
              if (!payload?.status) {
                return AgentTypesActions.SaveAgentTypeAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to save agent type',
                    data: null,
                  },
                });
              }
              return AgentTypesActions.SaveAgentTypeAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Agent type saved successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((err) =>
              of(
                AgentTypesActions.SaveAgentTypeAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteAgentType = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentTypesActions.DeleteAgentTypeAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeleteAgentTypeResponse>({
            mutation: DELETE_AGENT_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.deleteAgentType;
              if (!payload?.status) {
                return AgentTypesActions.DeleteAgentTypeAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to delete agent type',
                    data: null,
                  },
                });
              }
              return AgentTypesActions.DeleteAgentTypeAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Agent type deleted successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((err) =>
              of(
                AgentTypesActions.DeleteAgentTypeAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getAgentType = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentTypesActions.GetAgentTypeAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetAgentTypeResponse>({
            query: GET_AGENT_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getAgentType;
              if (!payload?.status || !payload?.data) {
                return AgentTypesActions.GetAgentTypeAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch agent type details',
                    data: null,
                  },
                });
              }
              return AgentTypesActions.GetAgentTypeAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? null,
                  data: payload.data,
                },
              });
            }),
            catchError((err) =>
              of(
                AgentTypesActions.GetAgentTypeAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
